import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core'
import { trigger, transition, style, animate } from '@angular/animations'
import { JsonPipe, NgClass } from '@angular/common'
import { TranslateModule } from '@ngx-translate/core'
import { FormGroup, ReactiveFormsModule } from '@angular/forms'
import { FormItem } from '../form-section/form-section-interface'
import { ErrorHandlerComponent } from '../error-handler/error-handler.component'
import { TooltipComponent } from '@app/shared/components/ui/tooltip/tooltip/tooltip.component'
import {
  FIELD_TYPE_DATE,
  FIELD_TYPE_DATEHEURE,
  FIELD_TYPE_DECIMAL,
  FIELD_TYPE_NUMBER,
  FIELD_TYPE_PASSWORD,
  FIELD_TYPE_TEXT,
} from '@app/core/services/inner-use/const/staticDefinitions'
import { FormErrorService } from '@app/shared/services/form-error.service'
import flatpickr from 'flatpickr'
import { French } from 'flatpickr/dist/l10n/fr.js'
import { AutoResizeDirective } from '@shared/directives'

@Component({
  selector: 'app-input-text',
  templateUrl: './input-text.component.html',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    NgClass,
    ErrorHandlerComponent,
    TooltipComponent,
    JsonPipe,
    AutoResizeDirective,
  ],
  animations: [
    trigger('fadeIcon', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.95)' }),
        animate('120ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [animate('100ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' }))]),
    ]),
  ],
})
export class InputTextComponent implements AfterViewInit, OnDestroy {
  @Input() formGroup: FormGroup
  @Output() blurred = new EventEmitter<Event>()
  @Input() fieldData: FormItem | null = null
  @Input() isDisabled = false
  @Input() order = ''
  @Input() isFormSubmitted = false
  @Input() isLargeText = false
  @Input() transparentBackground = false

  protected readonly FIELD_TYPE_TEXT = FIELD_TYPE_TEXT
  protected readonly FIELD_TYPE_PASSWORD = FIELD_TYPE_PASSWORD
  protected readonly FIELD_TYPE_DATE = FIELD_TYPE_DATE
  protected readonly FIELD_TYPE_DATEHEURE = FIELD_TYPE_DATEHEURE

  private readonly formErrorService = inject<FormErrorService>(FormErrorService)

  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private fpInstance?: any

  passwordVisible = false

  ngAfterViewInit(): void {
    if (!this.inputEl || !this.fieldData) return
    const type = this.fieldData.type
    if (type !== FIELD_TYPE_DATE && type !== FIELD_TYPE_DATEHEURE) return

    const currentValue = this.formGroup?.get(this.fieldData.controlName)?.value
    const isDatetime = type === FIELD_TYPE_DATEHEURE

    this.fpInstance = flatpickr(this.inputEl.nativeElement, {
      locale: French,
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: isDatetime ? 'd/m/Y H:i' : 'd/m/Y',
      enableTime: isDatetime,
      time_24hr: true,
      clickOpens: !this.isDisabled,
      allowInput: !this.isDisabled,
      defaultDate: currentValue || undefined,
      onChange: (selectedDates: Date[]) => {
        if (selectedDates.length === 0) return
        const d = selectedDates[0]
        let isoValue: string
        if (isDatetime) {
          isoValue = d.toISOString()
        } else {
          const yyyy = d.getFullYear()
          const mm = String(d.getMonth() + 1).padStart(2, '0')
          const dd = String(d.getDate()).padStart(2, '0')
          isoValue = `${yyyy}-${mm}-${dd}`
        }
        this.formGroup?.get(this.fieldData!.controlName)?.setValue(isoValue, { emitEvent: true })
      },
    })

    if (this.isDisabled && this.fpInstance?.altInput) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const altInput = (this.fpInstance as any).altInput
      altInput.disabled = true
    }
  }

  ngOnDestroy(): void {
    this.fpInstance?.destroy()
  }

  // Méthode pour retourner le type d'input en fonction du type défini
  getFieldType(type: string): string {
    switch (type) {
      case FIELD_TYPE_NUMBER:
      case FIELD_TYPE_DECIMAL:
        return 'number'
      case FIELD_TYPE_PASSWORD:
        return 'password'
      case FIELD_TYPE_DATE:
        return 'date'
      case FIELD_TYPE_DATEHEURE:
        return 'datetime-local'
      default:
        return 'text'
    }
  }

  getInputType(type: string): string {
    if (type === FIELD_TYPE_PASSWORD) {
      return this.passwordVisible ? 'text' : 'password'
    }
    if (type === FIELD_TYPE_DATEHEURE || type === FIELD_TYPE_DATE) {
      return 'text'
    }
    return this.getFieldType(type)
  }

  getDisplayValue(type: string, value: string): string {
    return value
  }

  togglePassword(): void {
    this.passwordVisible = !this.passwordVisible
  }

  onInput(event: Event): void {
    if (!this.fieldData || !this.formGroup) return
    const type = this.fieldData.type
    if (type === FIELD_TYPE_DATE || type === FIELD_TYPE_DATEHEURE) return
    const input = event.target as HTMLInputElement
    let value = input.value
    if (this.fieldData.pattern) {
      const regex = new RegExp(this.fieldData.pattern)
      let filtered = ''
      for (const char of value) {
        const candidate = filtered + char
        if (regex.test(candidate)) filtered = candidate
      }
      if (filtered !== value) {
        value = filtered
        input.value = value
      }
    }
    this.formGroup.get(this.fieldData.controlName)?.setValue(value, { emitEvent: true })
  }

  openCalendar(): void {
    this.fpInstance?.open()
  }

  /** Label text to display. Falls back to placeholder. Empty string hides the label. */
  get displayLabel(): string {
    return this.fieldData?.label !== undefined ? this.fieldData.label : (this.fieldData?.placeholder ?? '')
  }

  shouldShowErrors(): boolean {
    if (!this.formGroup || !this.fieldData) return false
    return this.formErrorService.shouldShowErrors(
      this.formGroup,
      {
        controlName: this.fieldData.controlName,
        minLength: this.fieldData.minLength,
        maxLength: this.fieldData.maxLength,
        errorMsg: this.fieldData.errorMsg,
      },
      this.isFormSubmitted
    )
  }
}
